import {Component, forwardRef, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ListBlock} from '../../../shared/models/form-schema.model';
import {BlockRendererComponent} from '../block-renderer/block-renderer.component';
import {FormFactoryService} from '../../../shared/services/form-factory.service';

@Component({
  selector: 'forms-list-block',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, forwardRef(() => BlockRendererComponent)],
  template: `
    <div class="list-block mb-6">
      <div class="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h3 *ngIf="block.label" class="text-lg font-bold text-gray-800 tracking-tight">{{ block.label }}</h3>
        <button (click)="addItem()" type="button" [disabled]="!canAdd()"
                class="px-4 py-2 bg-theme-primary-50 text-theme-primary-600 rounded-xl hover:bg-theme-primary-100 transition-all duration-200 text-sm font-bold active:scale-95 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100">
          + Eintrag hinzufügen
        </button>
      </div>

      <div class="list-items-container space-y-4">
        @for (itemGroup of array.controls; track i; let i = $index) {
          <div class="list-item border border-gray-100 rounded-2xl p-4 bg-white shadow-sm hover:shadow-md transition-all duration-200 relative">
            <span class="absolute top-4 left-4 text-xs font-bold text-gray-400">{{ i + 1 }}.</span>
            <button *ngIf="canRemove()" (click)="removeItem(i)" type="button"
                    [attr.aria-label]="'Eintrag ' + (i + 1) + ' entfernen'"
                    class="absolute top-4 right-4 text-red-400 hover:text-red-600 transition-colors">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                 <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
               </svg>
            </button>
            <div class="space-y-4 pt-6">
              @for (subBlock of block.blocks; track subBlock.id) {
                <forms-block-renderer
                  [block]="subBlock"
                  [parentForm]="getFormGroup(itemGroup)">
                </forms-block-renderer>
              }
            </div>
          </div>
        }
      </div>

      <div *ngIf="array.length === 0" class="text-sm text-gray-500 italic py-6 border-2 border-dashed border-gray-100 rounded-2xl text-center">
        Noch keine Einträge. Klicken Sie auf „Eintrag hinzufügen“, um zu beginnen.
      </div>

      <p *ngIf="!canAdd()" class="mt-2 text-xs font-semibold text-gray-400">
        Mehr als {{ block.maxItems }} Einträge sind nicht möglich.
      </p>
    </div>
  `
})
export class ListBlockComponent {
  @Input() block!: ListBlock;
  @Input() array!: FormArray;

  constructor(private formFactory: FormFactoryService) {
  }

  /** False once the declared {@link ListBlock.maxItems} is reached (absent ⇒ unlimited). */
  canAdd(): boolean {
    return this.block.maxItems === undefined || this.array.length < this.block.maxItems;
  }

  /** False while removing a row would drop below the declared {@link ListBlock.minItems}. */
  canRemove(): boolean {
    return this.array.length > (this.block.minItems ?? 0);
  }

  addItem() {
    if (!this.canAdd()) return;
    this.array.push(this.formFactory.createListItem(this.block));
  }

  removeItem(index: number) {
    if (!this.canRemove()) return;
    this.array.removeAt(index);
  }

  getFormGroup(control: any): FormGroup {
    return control as FormGroup;
  }
}
